import React, { useEffect, useState } from 'react';
import { Table, Form, Button, Spinner, Alert } from 'react-bootstrap';
import useAffiliations from 'hooks/userManagement/useAffiliations';
import { toast } from 'react-toastify';

const AddAffiliationForm = ({ userId, onAdded, onClose }) => {
  const { fetchAffiliations, loading, error } = useAffiliations();
  const [affiliations, setAffiliations] = useState([]);
  const [affiliationId, setAffiliationId] = useState('');
  const [status, setStatus] = useState('SELF_DECLARED');
  const [verifiedBy, setVerifiedBy] = useState('system');
  const [verifiedAt, setVerifiedAt] = useState('');
  const [visibility, setVisibility] = useState('PRIVATE');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Fetch affiliations and populate options for the select input
  useEffect(() => {
    const loadAffiliations = async () => {
      try {
        const data = await fetchAffiliations();
        setAffiliations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch affiliations', err);
        setLocalError('Failed to load affiliations');
      }
    };
    loadAffiliations();
  }, [fetchAffiliations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError(null);

    if (!userId) {
      setLocalError('User ID is required');
      return;
    }

    if (!affiliationId) {
      setLocalError('Please select an affiliation');
      return;
    }

    try {
      setIsSubmitting(true);

      setVerifiedBy('system');
      setVerifiedAt('');
      setStatus('SELF_DECLARED');

      const assignmentData = {
        affiliationId,
        status,
        verifiedBy,
        verifiedAt: verifiedAt || null,
        visibility
      };

      // Call the onAdded callback with the assignment data
      await onAdded(assignmentData);
      
      // Reset form
      setAffiliationId('');
      setStatus('SELF_DECLARED');
      setVerifiedBy('system');
      setVerifiedAt('');
      setVisibility('PRIVATE');
      
      toast.success('Affiliation assigned successfully');
      
      // Close modal if onClose is provided
      if (onClose) {
        onClose();
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to assign affiliation';
      setLocalError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {(localError || error) && (
        <Alert variant="danger" className="mb-3">
          {localError || error}
        </Alert>
      )}

      <Table borderless className="fs-10 mb-3">
        <tbody>
          <tr>
            <td className="py-2 fw-bold text-nowrap" style={{ width: '140px' }}>
              <Form.Label className="mb-0">Affiliation</Form.Label>
            </td>
            <td className="py-2">
              <Form.Select
                value={affiliationId}
                onChange={(e) => setAffiliationId(e.target.value)}
                disabled={isSubmitting || loading}
                className="fs-10"
                required
              >
                <option value="">Select an Affiliation</option>
                {affiliations.map((aff) => (
                  <option key={aff.id} value={aff.id}>
                    {aff.displayName || aff.code}
                  </option>
                ))}
              </Form.Select>
            </td>
          </tr>
          <tr>
            <td className="py-2 fw-bold text-nowrap">
              <Form.Label className="mb-0">Status</Form.Label>
            </td>
            <td className="py-2">
              <Form.Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                disabled={isSubmitting}
                className="fs-10"
                required
              >
                <option value="SELF_DECLARED">SELF_DECLARED</option>
              </Form.Select>
            </td>
          </tr>
          <tr>
            <td className="py-2 fw-bold text-nowrap">
              <Form.Label className="mb-0">Visibility</Form.Label>
            </td>
            <td className="py-2">
              <Form.Select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                disabled={isSubmitting}
                className="fs-10"
                required
              >
                <option value="PRIVATE">PRIVATE</option>
                <option value="HELPERS_ONLY">HELPERS_ONLY</option>
                <option value="PUBLIC">PUBLIC</option>
              </Form.Select>
            </td>
          </tr>
        </tbody>
      </Table>

      <div className="d-flex gap-2 justify-content-end mt-3">
        <Button
          variant="secondary"
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="fs-10"
        >
          Cancel
        </Button>
        <Button
          variant="success"
          type="submit"
          disabled={loading || isSubmitting}
          className="fs-10"
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            'Assign Affiliation'
          )}
        </Button>
      </div>
    </Form>
  );
};

export default AddAffiliationForm;
